import { useFormContext } from 'react-hook-form';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { actionsTypeOptions, tagScopeOptions } from './actionOptions';
import { FC, ReactNode, useMemo } from 'react';
import {
  ActionTypeEnum,
  TaskDefinitionActionType,
  TaskStatuses,
} from '@dynamika-shared/types/pmTaskDefinitions';
import { TrashIcon } from '@dynamika-shared/ui/icons';
import { selectProps } from '../formProps';
import { useClinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { TaskArgumentAction } from './TaskArgumentAction';
import { UserInfo } from '@components/UserInfo/UserInfo';
import { CustomOptionType } from '@components/OptionRenderer/OptionRenderer';
import { ClinicalTrialInventoryReaderType } from '@dynamika-shared/types/clinicalTrialInventory';
import { taskStatusesOptions } from 'src/pages/ClinicalTrial/Readings/TaskDefinition/TaskDefinitionForm/components/TaskCondinions/conditionOptions';
import { MetadataFields } from 'src/pages/ClinicalTrial/Readings/TaskDefinition/TaskDefinitionForm/components/TaskActions/MetadataFields';
import { AttachToParentTask } from './AttachToParentTask';
import { sortOptionsByLabel } from 'src/utils/sortOptions';

type TaskActionsProps = {
  children?: ReactNode;
  className?: string;
  index: number;
};

const optionRender = (option: CustomOptionType) => {
  const reader = option.data as ClinicalTrialInventoryReaderType;
  return (
    <UserInfo
      fullName={reader.name}
      email={reader.alias}
      enabled
      className={'max-w-[calc(100%-2rem)]'}
      classNameText={'max-w-[calc(100%-2rem)]'}
    />
  );
};

export const TaskActions: FC<TaskActionsProps> = ({ index }) => {
  const { setValue, watch } = useFormContext();
  const { readers, scoringForms, reportTemplates, functions, tags } =
    useClinicalTrialInventorySlice(state => state);

  const actions = watch('actions');
  const currentAction = actions.actions[index] as TaskDefinitionActionType;

  const optionReaders = useMemo(
    () =>
      readers
        .map(u => ({ label: u.name, value: u.id, ...u }))
        .sort(sortOptionsByLabel),
    [readers]
  );

  const valueOptions = useMemo(() => {
    switch (currentAction.type) {
      case ActionTypeEnum.Report:
        return reportTemplates
          .map(el => ({ value: el.id, label: el.name }))
          .sort(sortOptionsByLabel);
      case ActionTypeEnum.Scoring:
        return scoringForms
          .map(el => ({ value: el.id, label: el.name }))
          .sort(sortOptionsByLabel);
      case ActionTypeEnum.Function:
        return functions
          .map(el => ({ value: el.id, label: el.name }))
          .sort(sortOptionsByLabel);
      default:
        return [];
    }
  }, [currentAction.type, reportTemplates, scoringForms, functions]);

  const selectedValue = useMemo(() => {
    switch (currentAction.type) {
      case ActionTypeEnum.Report:
        return currentAction.templateId;
      case ActionTypeEnum.Scoring:
        return currentAction.scoringFormId;
      case ActionTypeEnum.Function:
        return currentAction.functionId;
      default:
        return undefined;
    }
  }, [currentAction]);

  const setRootType = (option: string) => {
    const updatedActions = actions.actions.map(
      (action: TaskDefinitionActionType, i: number) =>
        i === index
          ? {
              type: option,
              ...(option === '.Scoring' && {
                initialStatus: TaskStatuses.CREATED,
              }),
            }
          : action
    );
    setValue('actions', { actions: updatedActions });
  };

  const setActionValue = (option: string) => {
    const updatedActions = actions.actions.map(
      (action: TaskDefinitionActionType, i: number) => {
        if (i === index) {
          switch (action.type) {
            case ActionTypeEnum.Report:
              return { type: action.type, templateId: option };
            case ActionTypeEnum.Scoring:
              return {
                type: action.type,
                scoringFormId: option,
                initialStatus: action.initialStatus,
              };
            case ActionTypeEnum.Function:
              return { type: action.type, functionId: option };
            default:
              return action;
          }
        }
        return action;
      }
    );
    setValue('actions', { actions: updatedActions });
  };

  const setReader = (readerId: string) => {
    setValue(`actions.actions[${index}].readerId`, readerId);
  };
  const setTag = (tagId: string) => {
    setValue(`actions.actions[${index}].tagId`, tagId);
  };
  const setInitialStatus = (status: TaskStatuses) => {
    setValue(`actions.actions[${index}].initialStatus`, status);
  };

  const removeAction = () => {
    const updatedActions = actions.actions.filter(
      (_: TaskDefinitionActionType, i: number) => i !== index
    );
    setValue('actions', { actions: updatedActions });
  };

  const addArgument = () => {
    setValue(`actions.actions[${index}].arguments`, [
      ...(watch('actions').actions[index]?.arguments || []),
      {},
    ]);
  };

  return (
    <div>
      <div className='bg-blueTone p-3 ps-2 rounded-sm flex justify-between'>
        <div className={'grid grid-cols-[150px_1fr] gap-4 flex-grow'}>
          <div>
            <div
              className={
                'text-1424 font-semibold field-required mt-2.5 max-w-[758px] w-[758px]'
              }
            >
              Action
            </div>
          </div>
          <div className='flex justify-between'>
            <div className='flex gap-3'>
              <Select
                {...selectProps}
                options={actionsTypeOptions}
                value={currentAction.type}
                onChange={setRootType}
                label={'Action Type'}
                placeholder={'Select Action Type'}
              />
              <Select
                {...selectProps}
                options={valueOptions}
                value={selectedValue}
                onChange={setActionValue}
                label={'Action Value'}
                placeholder={'Select Action Value'}
                autoDropdownWidth
              />
              {currentAction.type === '.Scoring' && (
                <Select
                  {...selectProps}
                  options={optionReaders}
                  value={currentAction.readerId}
                  onChange={setReader}
                  optionRender={optionRender}
                  label={'Reader'}
                  placeholder={'Select Reader'}
                  autoDropdownWidth
                />
              )}
            </div>
            <div className={'mx-3'}>
              {currentAction.type === '.Scoring' && (
                <Select
                  {...selectProps}
                  options={taskStatusesOptions}
                  value={currentAction.initialStatus}
                  onChange={setInitialStatus}
                  label={'Status'}
                  placeholder={'Select status'}
                />
              )}
            </div>
          </div>
          <div></div>
          <div className={'flex flex-col gap-3'}>
            <div className='relative w-full p-3 pt-1 pb-[20px] rounded-md border border-brightGray bg-[#F6F9FF]'>
              <h2 className={'mt-2 text-1624 font-medium text-vividTangelo'}>
                Metadata
              </h2>
              <MetadataFields index={index} />
            </div>
            <AttachToParentTask index={index} />
            <div className='relative w-full p-3 pt-1 pb-[20px] rounded-md border border-brightGray bg-[#F6F9FF]'>
              <h2 className={'mt-2 text-1624 font-medium text-vividTangelo'}>
                Arguments
              </h2>
                {actions.actions[index].arguments?.map(
                  (_: TaskDefinitionActionType, i: number) => (
                    <div
                      className='rounded-md border border-gray-300 p-2 m-2'
                      key={`actions[${index}].arguments[${i}]`}
                    >
                      <TaskArgumentAction
                        actionIndex={index}
                        argumentIndex={i}
                      />
                    </div>
                  )
                )}
            {currentAction.type && (
              <div className={'flex justify-between flex-wrap'}>
                <Button
                  type={'text'}
                  className={'text-1424 text-vividTangelo font-medium'}
                  onClick={addArgument}
                >
                  + Add Argument
                </Button>

                <div className={'flex gap-3 flex-wrap'}>
                  <Select
                    {...selectProps}
                    inOneLine
                    options={tagScopeOptions}
                    value={currentAction.tagScope}
                    onChange={tagScope =>
                      setValue(`actions.actions[${index}].tagScope`, tagScope)
                    }
                    label={'Tag Scope'}
                    placeholder={'Tag Scope'}
                    className={'grow w-[20.6rem]'}
                    classNameInputWrapper={'ml-3 mr-3'}
                  />
                  <Select
                    {...selectProps}
                    inOneLine
                    options={tags
                      .map(t => ({ label: t.name, value: t.id }))
                      .sort(sortOptionsByLabel)}
                    value={currentAction.tagId}
                    onChange={setTag}
                    label={'Result (Tag)'}
                    placeholder={'Result (Tag)'}
                    className={'grow'}
                    classNameInputWrapper={'ml-3 mr-3'}
                    autoDropdownWidth
                  />
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
        <TrashIcon
          className='cursor-pointer hover:text-redOrange mt-3 min-w-3'
          width={24}
          height={24}
          onClick={removeAction}
        />
      </div>
    </div>
  );
};
